import React, { useRef, useEffect } from "react";
import {
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Select,
} from "antd";
import Icon from "@mdi/react";
import { mdiMenuDown } from "@mdi/js";
import { b2c2bDateFormat } from "../../../utils/antd.props";

export const DESIGNATION_OPTIONS = ["Mr", "Mrs", "Ms", "Mstr", "Mdm"].map(
  (d) => ({ value: d, label: d }),
);

export const RELATIONSHIP_OPTIONS = [
  "Spouse",
  "Father",
  "Mother",
  "Brother",
  "Daughter",
  "Sister",
  "Son",
  "Grandmother",
  "Grandfather",
  "Grandson",
  "Granddaughter",
  "Friend",
  "Others",
].map((r) => ({ value: r, label: r }));

export const SHARE_OPTIONS = ["0", "25", "50", "75", "100"].map((s) => ({
  value: s,
  label: `${s}%`,
}));

type NamePath = (string | number)[];

interface InsuranceSectionProps {
  namePrefix: NamePath;
  fieldPath: NamePath;
  guardianType: "minor" | "infant";
  guardianOptions?: { value: any; label: string }[];
  parentGuestId?: number | null;
  insurance: {
    gd_benefits?: string;
    gd_policy?: string;
    gd_endorsement?: string;
  } | null;
  disabled: boolean;
}

const getVal = (obj: any, path: NamePath) =>
  path.reduce((acc, key) => acc?.[key], obj);

const GuardianForm: React.FC<{ namePrefix: NamePath; disabled: boolean }> = ({
  namePrefix,
  disabled,
}) => (
  <>
    <Divider
      orientation="left"
      orientationMargin="0"
      style={{ color: "#1677ff" }}
    >
      Guardian
    </Divider>
    <Row gutter={[20, 20]}>
      <Col xs={24} lg={12}>
        <Form.Item label="Designation" name={[...namePrefix, "ph_designation"]}>
          <Select
            disabled={disabled}
            variant="filled"
            suffixIcon={<Icon path={mdiMenuDown} size={1} />}
            placeholder="Select"
            options={DESIGNATION_OPTIONS}
          />
        </Form.Item>
      </Col>
      <Col xs={24} lg={12}>
        <Form.Item label="Full Name" name={[...namePrefix, "ph_name"]}>
          <Input disabled={disabled} placeholder="Full name" />
        </Form.Item>
      </Col>
      <Col xs={24} lg={12}>
        <Form.Item label="Date of Birth" name={[...namePrefix, "ph_dob"]}>
          <DatePicker
            disabled={disabled}
            format={b2c2bDateFormat}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} lg={12}>
        <Form.Item
          label="Passport Number"
          name={[...namePrefix, "ph_id_number"]}
        >
          <Input disabled={disabled} placeholder="Passport Number" />
        </Form.Item>
      </Col>
    </Row>
  </>
);

const ShareErrorScroller: React.FC<{ targetRef: React.RefObject<HTMLDivElement> }> = ({ targetRef }) => {
  useEffect(() => {
    const anchor = targetRef.current?.closest<HTMLElement>('[data-insurance-anchor]') ?? targetRef.current;
    anchor?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  return null;
};

export const InsuranceSection: React.FC<InsuranceSectionProps> = ({
  namePrefix,
  fieldPath,
  guardianType,
  guardianOptions,
  parentGuestId,
  insurance,
  disabled,
}) => {
  const form = Form.useFormInstance();
  const shareErrorRef = useRef<HTMLDivElement>(null);
  return (
  <div data-insurance-anchor>
    <Divider orientation="left" orientationMargin="0">
      Travel Insurance
    </Divider>

    {/* Insurance description + confirmation radio */}
    <div className="bookings-page_documents">
      <span>
        Our packages bundle up with Chubb Executive travel insurance that is
        underwritten by Chubb Insurance Malaysia Berhad. Please choose your
        option below.
      </span>
      <span>
        Click{" "}
        <a href={insurance?.gd_benefits} target="_blank">
          here
        </a>{" "}
        to view the benefits wording
      </span>
      <span>
        Click{" "}
        <a href={insurance?.gd_policy} target="_blank">
          here
        </a>{" "}
        to view the policy wording
      </span>
      <span>
        Click{" "}
        <a href={insurance?.gd_endorsement} target="_blank">
          here
        </a>{" "}
        to view endorsement 1
      </span>
    </div>
    <div className="bookings-page_notes" style={{ marginTop: 12 }}>
      <Form.Item noStyle name={[...namePrefix, "insurance_confirmation"]}>
        <Radio.Group
          disabled={disabled}
          options={[
            {
              value: true,
              label: "Yes, please include with no additional charge",
            },
            {
              value: false,
              label:
                "No, I don't want free insurance and understand the cost remain the same",
            },
          ]}
        />
      </Form.Item>
    </div>

    {/* Death Note Checkbox */}
    <div style={{ marginTop: 12 }}>
      <Form.Item
        noStyle
        name={[...namePrefix, "insurance_nomination_flag"]}
        valuePropName="checked"
      >
        <Checkbox disabled={disabled}>
          Note: Death benefit will be paid to insured Person's estate. Please
          uncheck if the insured person wishes to make a NOMINEE/NOMINEES.
        </Checkbox>
      </Form.Item>
    </div>

    {/* Guardian Section */}
    {guardianType === "minor" ? (
      /* Minor: shows guardian selector only when guest is under 18 */
      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) =>
          getVal(prev, fieldPath)?.over_18 !== getVal(curr, fieldPath)?.over_18
        }
      >
        {({ getFieldValue }) => {
          const isMinor = getFieldValue([...fieldPath, "over_18"]) === false;
          return isMinor ? (
            <div style={{ marginTop: 16 }}>
              <Row gutter={[20, 20]}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    label="Select Guardian"
                    name={[...namePrefix, "guardian_id"]}
                  >
                    <Select
                      disabled={disabled}
                      variant="filled"
                      suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                      placeholder="Select"
                      options={guardianOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  getVal(prev, fieldPath)?.guardian_id !==
                  getVal(curr, fieldPath)?.guardian_id
                }
              >
                {({ getFieldValue }) => {
                  const guardianId = getFieldValue([
                    ...fieldPath,
                    "guardian_id",
                  ]);
                  return guardianId === 0 ? (
                    <GuardianForm namePrefix={namePrefix} disabled={disabled} />
                  ) : null;
                }}
              </Form.Item>
            </div>
          ) : null;
        }}
      </Form.Item>
    ) : (
      /* Infant: shows parent radio + manual guardian form when "No" */
      <div style={{ marginTop: 16 }}>
        <Row gutter={[20, 20]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Is Guest the parent?"
              name={[...namePrefix, "parent_id"]}
            >
              <Radio.Group disabled={disabled}>
                <Radio value={parentGuestId}>Yes</Radio>
                <Radio value={0}>No</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            getVal(prev, fieldPath)?.parent_id !==
            getVal(curr, fieldPath)?.parent_id
          }
        >
          {({ getFieldValue }) => {
            const parentId = getFieldValue([...fieldPath, "parent_id"]);
            return parentId === 0 ? (
              <GuardianForm namePrefix={namePrefix} disabled={disabled} />
            ) : null;
          }}
        </Form.Item>
      </div>
    )}

    {/* Nominees + Witness — shown when death note is UNCHECKED */}
    <Form.Item
      noStyle
      shouldUpdate={(prev, curr) =>
        getVal(prev, fieldPath)?.insurance_nomination_flag !==
        getVal(curr, fieldPath)?.insurance_nomination_flag
      }
    >
      {({ getFieldValue }) => {
        const nominationFlag = getFieldValue([
          ...fieldPath,
          "insurance_nomination_flag",
        ]);
        return nominationFlag === false ? (
          <>
            <Form.List name={[...namePrefix, "insurance_nomination"]}>
              {(nomineeFields) => (
                <>
                  {nomineeFields.map((nomineeField, nomineeIndex) => (
                    <div key={nomineeField.key}>
                      <Divider
                        orientation="left"
                        orientationMargin="0"
                        style={{ color: "#1677ff" }}
                      >
                        Nominee Details {nomineeIndex + 1}
                      </Divider>
                      <Row gutter={[20, 20]}>
                        <Col xs={24} lg={8}>
                          <Form.Item
                            label="Surname"
                            name={[nomineeField.name, "nominee_surname"]}
                          >
                            <Input disabled={disabled} placeholder="Surname" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={8}>
                          <Form.Item
                            label="Given Name"
                            name={[nomineeField.name, "nominee_given_name"]}
                          >
                            <Input
                              disabled={disabled}
                              placeholder="Given Name"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={8}>
                          <Form.Item
                            label="Passport/NRIC Number"
                            name={[
                              nomineeField.name,
                              "nominee_passport_number",
                            ]}
                          >
                            <Input
                              disabled={disabled}
                              placeholder="Passport/NRIC Number"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={8}>
                          <Form.Item
                            label="Date of Birth"
                            name={[nomineeField.name, "nominee_date_of_birth"]}
                          >
                            <DatePicker
                              disabled={disabled}
                              format={b2c2bDateFormat}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={8}>
                          <Form.Item
                            label="Relationship"
                            name={[nomineeField.name, "nominee_relationship"]}
                          >
                            <Select
                              disabled={disabled}
                              variant="filled"
                              suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                              placeholder="Select"
                              options={RELATIONSHIP_OPTIONS}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={8}>
                          <Form.Item noStyle shouldUpdate>
                            {() => {
                              const nominations: any[] =
                                form.getFieldValue([
                                  ...fieldPath,
                                  "insurance_nomination",
                                ]) || [];
                              const otherTotal = nominations.reduce(
                                (sum: number, n: any, i: number) =>
                                  i !== nomineeField.name
                                    ? sum + (parseInt(n?.nominee_share) || 0)
                                    : sum,
                                0,
                              );
                              const remaining = 100 - otherTotal;
                              const availableOptions = SHARE_OPTIONS.map(
                                (opt) => ({
                                  ...opt,
                                  disabled: parseInt(opt.value) > remaining,
                                }),
                              );
                              const total = nominations.reduce(
                                (sum: number, n: any) =>
                                  sum + (parseInt(n?.nominee_share) || 0),
                                0,
                              );
                              const hasAnyNominee = nominations.some(
                                (n: any) =>
                                  n?.nominee_surname || n?.nominee_given_name,
                              );
                              const shareError = hasAnyNominee && total !== 100;
                              return (
                                <Form.Item
                                  label="Share (%)"
                                  name={[nomineeField.name, "nominee_share"]}
                                  rules={[
                                    {
                                      validator(_, value) {
                                        const val = parseInt(value) || 0;
                                        const allNominations: any[] =
                                          form.getFieldValue([
                                            ...fieldPath,
                                            "insurance_nomination",
                                          ]) || [];
                                        const currentOtherTotal =
                                          allNominations.reduce(
                                            (sum: number, n: any, i: number) =>
                                              i !== nomineeField.name
                                                ? sum +
                                                  (parseInt(n?.nominee_share) ||
                                                    0)
                                                : sum,
                                            0,
                                          );
                                        if (val + currentOtherTotal > 100) {
                                          return Promise.reject(
                                            `Exceeds remaining ${
                                              100 - currentOtherTotal
                                            }%`,
                                          );
                                        }
                                        return Promise.resolve();
                                      },
                                    },
                                  ]}
                                >
                                  <Select
                                    disabled={disabled}
                                    variant="filled"
                                    status={shareError ? "error" : undefined}
                                    suffixIcon={
                                      <Icon path={mdiMenuDown} size={1} />
                                    }
                                    placeholder="Select"
                                    options={availableOptions}
                                  />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </>
              )}
            </Form.List>

            {/* Share total — scroll anchor + blocks form submission */}
            <div ref={shareErrorRef}>
              <Form.Item
                style={{ margin: 0, padding: 0, height: 0, overflow: "hidden" }}
                name={[...namePrefix, "_share_total_check"]}
                rules={[
                  {
                    validator() {
                      const nominations: any[] =
                        form.getFieldValue([
                          ...fieldPath,
                          "insurance_nomination",
                        ]) || [];
                      const hasAnyNominee = nominations.some(
                        (n: any) => n?.nominee_surname || n?.nominee_given_name,
                      );
                      if (!hasAnyNominee) return Promise.resolve();
                      const total = nominations.reduce(
                        (sum: number, n: any) =>
                          sum + (parseInt(n?.nominee_share) || 0),
                        0,
                      );
                      if (total !== 100) {
                        return Promise.reject(
                          `Total share must equal 100%. Current total: ${total}%`,
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>

              {/* Share proportion validation — visual display */}
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const nominations =
                    getFieldValue([...fieldPath, "insurance_nomination"]) || [];
                  const total = nominations.reduce(
                    (sum: number, n: any) =>
                      sum + (parseInt(n?.nominee_share) || 0),
                    0,
                  );
                  const hasAnyNominee = nominations.some(
                    (n: any) => n?.nominee_surname || n?.nominee_given_name,
                  );
                  if (hasAnyNominee && total !== 100) {
                    return (
                      <>
                        <ShareErrorScroller targetRef={shareErrorRef} />
                        <Form.Item
                          style={{ marginBottom: 8 }}
                          validateStatus="error"
                          help={`Total share proportion must equal 100%. Current total: ${total}%`}
                        />
                      </>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </div>

            {/* Witness Details */}
            <Divider
              orientation="left"
              orientationMargin="0"
              style={{ color: "#1677ff" }}
            >
              Witness Details
            </Divider>
            <Row gutter={[20, 20]}>
              <Col xs={24} lg={8}>
                <Form.Item
                  label="Surname"
                  name={[...namePrefix, "witness_surname"]}
                  rules={[{ required: true }]}
                >
                  <Input disabled={disabled} placeholder="Surname" />
                </Form.Item>
              </Col>
              <Col xs={24} lg={8}>
                <Form.Item
                  label="Given Name"
                  name={[...namePrefix, "witness_given_name"]}
                  rules={[{ required: true }]}
                >
                  <Input disabled={disabled} placeholder="Given Name" />
                </Form.Item>
              </Col>
              <Col xs={24} lg={8}>
                <Form.Item
                  label="Passport/NRIC Number"
                  name={[...namePrefix, "witness_passport_number"]}
                  rules={[{ required: true }]}
                >
                  <Input
                    disabled={disabled}
                    placeholder="Passport/NRIC Number"
                  />
                </Form.Item>
              </Col>
            </Row>
            <div style={{ marginTop: 8 }}>
              <small>
                By typing in my name and Passport/NRIC No., I confirm that:
                <ol>
                  <li>
                    I am of sound mind and have attained the age of 18 years
                    old;
                  </li>
                  <li>I am not a nominee named by the Insured Person(s);</li>
                  <li>
                    I am the witness to the nomination(s) made, as the
                    nomination(s) was(were) made to me by the respective Insured
                    Person(s); and
                  </li>
                  <li>
                    I have informed the Insured Person(s) that if the nomination
                    does not create a trust policy, and he/she intends his/her
                    nominee(s) to receive the policy benefits beneficially and
                    not as an executor, he/she has to assign the policy benefits
                    to his/her nominee(s).
                  </li>
                </ol>
              </small>
            </div>
          </>
        ) : null;
      }}
    </Form.Item>
  </div>
  );
};
